import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Loader2, TrendingUp, Calendar as CalendarIcon, Trophy, DollarSign } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { toast } from "sonner";

const EUR = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const EUR_FULL = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" });

export default function RevenueCharts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/stats/monthly", { params: { months: 12 } });
        setData(data);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return (
    <section className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-400" data-testid="revenue-charts-loading">
      <Loader2 className="w-6 h-6 mx-auto animate-spin" />
    </section>
  );
  if (!data) return null;

  const monthly = data.monthly || [];
  const currentMonth = monthly[monthly.length - 1] || { revenue: 0, jobs: 0, label: "" };
  const avg = monthly.length ? monthly.reduce((s, m) => s + m.revenue, 0) / monthly.length : 0;
  const topJob = data.top_job_of_month;

  // Highlight current month in bars
  const maxRev = Math.max(1, ...monthly.map((m) => m.revenue));

  return (
    <section className="space-y-6" data-testid="revenue-charts">
      <div className="flex items-center gap-2">
        <TrendingUp className="w-5 h-5 text-emerald-600" />
        <h2 className="font-display text-xl font-bold text-slate-900">Andamento Incassi (ultimi 12 mesi)</h2>
      </div>

      {/* 3 KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 text-white rounded-2xl p-5 shadow-lg" data-testid="kpi-month-revenue">
          <div className="flex items-center gap-2 text-emerald-100 text-xs font-bold uppercase tracking-widest">
            <DollarSign className="w-4 h-4" /> Incassi Mese Corrente
          </div>
          <div className="font-display text-3xl sm:text-4xl font-extrabold mt-2">{EUR.format(currentMonth.revenue)}</div>
          <div className="text-xs text-emerald-100 font-mono mt-1">{currentMonth.label} · {currentMonth.jobs} trasloch{currentMonth.jobs === 1 ? "o" : "i"} completati</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid="kpi-avg-monthly">
          <div className="flex items-center gap-2 text-slate-500 text-xs font-bold uppercase tracking-widest">
            <CalendarIcon className="w-4 h-4" /> Media Mensile
          </div>
          <div className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-2">{EUR.format(avg)}</div>
          <div className="text-xs text-slate-400 font-mono mt-1">Su {monthly.length} mesi</div>
        </div>
        <div className="bg-white rounded-2xl border border-amber-200 p-5 shadow-sm" data-testid="kpi-top-job">
          <div className="flex items-center gap-2 text-amber-600 text-xs font-bold uppercase tracking-widest">
            <Trophy className="w-4 h-4" /> Top Mese
          </div>
          {topJob ? (
            <>
              <div className="font-display text-2xl font-extrabold text-slate-900 mt-2 truncate">{EUR_FULL.format(topJob.revenue)}</div>
              <div className="text-xs text-slate-500 mt-1 truncate font-semibold">{topJob.client_name}</div>
              <div className="text-[11px] text-slate-400 font-mono">{topJob.scheduled_date}</div>
            </>
          ) : (
            <div className="text-sm text-slate-400 italic mt-2">Nessun trasloco questo mese</div>
          )}
        </div>
      </div>

      {/* Revenue bar chart */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid="chart-revenue-bars">
        <div className="font-display font-bold text-slate-900 mb-3">Incassi mensili</div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={monthly} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
            <Tooltip
              formatter={(v) => [EUR_FULL.format(v), "Incassi"]}
              contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
            />
            <Bar dataKey="revenue" radius={[8, 8, 0, 0]}>
              {monthly.map((m, i) => (
                <Cell key={i} fill={i === monthly.length - 1 ? "#10b981" : "#0ea5e9"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Jobs line chart */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid="chart-jobs-line">
        <div className="font-display font-bold text-slate-900 mb-3">Traslochi completati</div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={monthly} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
            <Line type="monotone" dataKey="jobs" stroke="#6366f1" strokeWidth={3} dot={{ r: 4, fill: "#6366f1" }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
